import React, { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Check, HelpCircle, Stethoscope } from "lucide-react";
import { HAIR_HUB, HAIR_GUIDES, getHairGuide } from "@/data/hairGuides";
import { useSeo, useJsonLd, SITE_URL } from "@/lib/seo";
import { OZENTYA_SHOP_URL } from "@/lib/social";

const Crumbs = ({ current }) => (
  <nav aria-label="Fil d'ariane" className="text-xs text-[var(--oz-muted)] mb-6">
    <Link to="/" className="inline-block py-2 hover:text-[var(--oz-text)]">Accueil</Link>
    <span className="mx-2">/</span>
    {current ? (
      <>
        <Link to={HAIR_HUB.path} className="inline-block py-2 hover:text-[var(--oz-text)]">Conseils cheveux afro</Link>
        <span className="mx-2">/</span>
        <span className="text-[var(--oz-text)]">{current}</span>
      </>
    ) : (
      <span className="text-[var(--oz-text)]">Conseils cheveux afro</span>
    )}
  </nav>
);

const Disclaimer = () => (
  <div data-testid="hair-disclaimer" className="mt-10 flex gap-3 rounded-2xl border border-[var(--oz-border)] bg-white p-5">
    <Stethoscope className="w-5 h-5 text-[var(--oz-gold)] flex-shrink-0 mt-0.5" />
    <p className="text-sm text-[var(--oz-muted)] leading-relaxed">
      Ces informations sont générales et ne remplacent pas l'avis d'un médecin ou d'un dermatologue. Les produits
      Ozentya sont des cosmétiques : ils ne soignent pas une maladie et aucun résultat n'est garanti.
    </p>
  </div>
);

const NetCta = ({ links, slug }) => (
  <div className="mt-12 rounded-[1.5rem] bg-[var(--oz-dark)] text-white p-8">
    <h2 className="font-serif text-2xl sm:text-3xl">Aller plus loin sur ozentya.net</h2>
    <p className="mt-2 text-sm text-white/70">Guides détaillés et soins Ozentya pour cheveux afro et crépus.</p>
    <div className="mt-6 grid sm:grid-cols-2 gap-3">
      {links.map((l, i) => (
        <a key={l.href} href={l.href} target="_blank" rel="noopener noreferrer" data-testid={`hair-net-link-${slug}-${i}`}
          className="group flex items-center justify-between gap-3 rounded-xl bg-white/5 border border-white/15 px-4 py-3.5 hover:border-[var(--oz-gold)] transition-colors">
          <span className="text-sm font-semibold">{l.label}</span>
          <ArrowUpRight className="w-4 h-4 text-[var(--oz-gold)] flex-shrink-0" />
        </a>
      ))}
    </div>
  </div>
);

export function HairGuidesHub() {
  useSeo({ title: HAIR_HUB.title, description: HAIR_HUB.description, path: HAIR_HUB.path });
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <div data-testid="hair-hub" className="px-4 sm:px-8 lg:px-16 max-w-4xl mx-auto py-12 md:py-16">
      <Crumbs />
      <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-[var(--oz-text)] leading-tight">{HAIR_HUB.h1}</h1>
      <p className="mt-6 text-lg text-[var(--oz-muted)] leading-relaxed">{HAIR_HUB.intro}</p>
      <div className="mt-10 grid gap-4">
        {HAIR_GUIDES.map((g) => (
          <Link key={g.slug} to={`${HAIR_HUB.path}/${g.slug}`} data-testid={`hair-hub-link-${g.slug}`}
            className="group flex items-center justify-between gap-4 rounded-2xl bg-white border border-[var(--oz-border)] px-6 py-5 hover:border-[var(--oz-gold)] transition-colors">
            <span>
              <span className="block font-serif text-xl text-[var(--oz-text)]">{g.h1}</span>
              <span className="block mt-1 text-sm text-[var(--oz-muted)]">{g.description}</span>
            </span>
            <ArrowRight className="w-5 h-5 text-[var(--oz-gold)] flex-shrink-0 group-hover:translate-x-0.5 transition-transform" />
          </Link>
        ))}
      </div>
      <a href={OZENTYA_SHOP_URL} target="_blank" rel="noopener noreferrer" data-testid="hair-hub-shop-link"
        className="mt-8 inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[var(--oz-gold)] text-[var(--oz-dark)] font-semibold text-sm">
        Voir les soins sur ozentya.net <ArrowUpRight className="w-4 h-4" />
      </a>
      <Disclaimer />
    </div>
  );
}

export function HairGuidePage() {
  const { slug } = useParams();
  const g = getHairGuide(slug);
  const path = `${HAIR_HUB.path}/${slug}`;
  useSeo({ title: g ? g.title : "Ozentya", description: g ? g.description : "", path });
  useJsonLd("ld-hair-guide", g ? {
    "@context": "https://schema.org",
    "@graph": [
      { "@type": "Article", headline: g.h1, description: g.description, inLanguage: "fr-FR", url: `${SITE_URL}${path}`,
        author: { "@type": "Organization", name: "Ozentya", url: `${SITE_URL}/a-propos` },
        publisher: { "@type": "Organization", name: "Ozentya", url: SITE_URL } },
      { "@type": "BreadcrumbList", itemListElement: [
        { "@type": "ListItem", position: 1, name: "Accueil", item: SITE_URL },
        { "@type": "ListItem", position: 2, name: "Conseils cheveux afro", item: `${SITE_URL}${HAIR_HUB.path}` },
        { "@type": "ListItem", position: 3, name: g.breadcrumb, item: `${SITE_URL}${path}` }] },
      { "@type": "FAQPage", mainEntity: g.faq.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) },
    ],
  } : null);
  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  if (!g) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center">
        <h1 className="font-serif text-3xl mb-4">Guide introuvable</h1>
        <Link to={HAIR_HUB.path} className="text-[var(--oz-gold)] font-semibold">Voir tous les conseils</Link>
      </div>
    );
  }
  const others = HAIR_GUIDES.filter((o) => o.slug !== slug);
  return (
    <div data-testid={`hair-guide-${g.slug}`} className="px-4 sm:px-8 lg:px-16 max-w-4xl mx-auto py-12 md:py-16">
      <Crumbs current={g.breadcrumb} />
      <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-[var(--oz-text)] leading-tight">{g.h1}</h1>
      <p className="mt-3 text-xs text-[var(--oz-muted)]">Par l'équipe <Link to="/a-propos" className="underline">Ozentya</Link></p>
      <p className="mt-6 text-lg text-[var(--oz-text)] leading-relaxed bg-[var(--oz-bg-2)] rounded-2xl p-5 border border-[var(--oz-border)]">{g.intro}</p>
      <article className="mt-10 space-y-9">
        {g.sections.map((s, i) => (
          <section key={i}>
            <h2 className="font-serif text-2xl sm:text-3xl text-[var(--oz-text)] mb-3">{s.h}</h2>
            {s.p && <p className="text-base text-[var(--oz-muted)] leading-relaxed">{s.p}</p>}
            {s.list && (
              <ul className="mt-3 space-y-2">
                {s.list.map((li, j) => (
                  <li key={j} className="flex gap-2.5 text-base text-[var(--oz-muted)] leading-relaxed">
                    <Check className="w-5 h-5 text-[var(--oz-gold)] flex-shrink-0 mt-0.5" /><span>{li}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        ))}
      </article>
      <Disclaimer />
      <NetCta links={g.netLinks} slug={g.slug} />
      <div className="mt-14">
        <h2 className="font-serif text-2xl sm:text-3xl text-[var(--oz-text)] mb-6 flex items-center gap-2">
          <HelpCircle className="w-6 h-6 text-[var(--oz-gold)]" /> Questions fréquentes
        </h2>
        <div className="space-y-4">
          {g.faq.map((f, i) => (
            <div key={i} className="bg-white border border-[var(--oz-border)] rounded-2xl p-5">
              <h3 className="font-semibold text-[var(--oz-text)]">{f.q}</h3>
              <p className="mt-2 text-sm text-[var(--oz-muted)] leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="mt-14 pt-10 border-t border-[var(--oz-border)] grid sm:grid-cols-2 gap-3">
        {others.map((o) => (
          <Link key={o.slug} to={`${HAIR_HUB.path}/${o.slug}`} data-testid={`hair-related-${o.slug}`}
            className="group flex items-center justify-between gap-3 bg-white border border-[var(--oz-border)] rounded-xl px-5 py-4 hover:border-[var(--oz-gold)] transition-colors">
            <span className="font-semibold text-[var(--oz-text)]">{o.breadcrumb}</span>
            <ArrowRight className="w-4 h-4 text-[var(--oz-gold)]" />
          </Link>
        ))}
      </div>
    </div>
  );
}
